import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
import InvisibleTeacher from "./InvisibleTeacher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grade levels K-12+
const GRADE_LEVELS = [
  { id: "K", name: "Kindergarten", age: 5 },
  { id: "1", name: "Grade 1", age: 6 },
  { id: "2", name: "Grade 2", age: 7 },
  { id: "3", name: "Grade 3", age: 8 },
  { id: "4", name: "Grade 4", age: 9 },
  { id: "5", name: "Grade 5", age: 10 },
  { id: "6", name: "Grade 6", age: 11 },
  { id: "7", name: "Grade 7", age: 12 },
  { id: "8", name: "Grade 8", age: 13 },
  { id: "9", name: "Grade 9", age: 14 },
  { id: "10", name: "Grade 10", age: 15 },
  { id: "11", name: "Grade 11", age: 16 },
  { id: "12", name: "Grade 12", age: 17 },
  { id: "12+", name: "Grade 12+", age: 18 }
];

// Tab 1: Academic Foundations (State Compliance)
const ACADEMIC_SUBJECTS = [
  {
    id: "mathematics",
    name: "Mathematics",
    icon: "📐",
    color: "#4169E1",
    description: "Grade-appropriate mathematical concepts and problem-solving"
  },
  {
    id: "language-arts",
    name: "Language Arts",
    icon: "📖",
    color: "#2E8B57",
    description: "Reading comprehension, writing skills, and literacy development"
  },
  {
    id: "science",
    name: "Science",
    icon: "🔬",
    color: "#9932CC",
    description: "Physical, Life, and Earth Sciences exploration"
  },
  {
    id: "social-studies",
    name: "Social Studies",
    icon: "🌍",
    color: "#CD853F",
    description: "US History, World History, Geography, and Civics"
  }
];

// Tab 2: The Zion Pillars (YKBN Doctrine)
const ZION_PILLARS = [
  {
    id: "phenotype-recognition",
    name: "Phenotype Recognition",
    icon: "👤",
    color: "#8B4513",
    subtitle: "Identity & Heritage",
    description: "Understanding true Israelite identity through physical characteristics and historical records"
  },
  {
    id: "sabbath-mechanics",
    name: "Sabbath Mechanics",
    icon: "📅",
    color: "#FFD700",
    subtitle: "The 8-15-22-29 Enochian Cycle",
    description: "Mastering the lunar Sabbath system as revealed in Ezekiel 46:1"
  },
  {
    id: "solar-observation",
    name: "Solar Observation",
    icon: "☀️",
    color: "#FF6347",
    subtitle: "The 4th Portal / 16-Hour Latitude Proofs",
    description: "Tracking the Sun's journey through the 6 gates and the 4th Portal trigger"
  },
  {
    id: "forensic-etymology",
    name: "Forensic Etymology",
    icon: "📜",
    color: "#DAA520",
    subtitle: "The Root & Branch Language Audit",
    description: "Stripping Roman/Greek transliterations to reveal Hebrew Parent Roots"
  }
];

function K12AcademicProgram({ user }) {
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [activeTab, setActiveTab] = useState("academic"); // "academic" or "zion"
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [error, setError] = useState("");

  // ═══════════════════════════════════════════════════════════════════════════
  // ADMIN AUTHORITY PROTOCOL
  // ═══════════════════════════════════════════════════════════════════════════
  // Admin has ZERO RESTRICTIONS - all content unlocked, no prerequisites
  // ═══════════════════════════════════════════════════════════════════════════
  const isAdmin = user?.username === "YKBN" || user?.email === "Yisraellearningcenter@gmail.com" || user?.is_admin;

  // Reset state when grade changes
  useEffect(() => {
    setSelectedSubject(null);
    setCurriculum(null);
    setSelectedLesson(null);
    setLessonContent(null);
    setError("");
  }, [selectedGrade]);

  // Reset lesson when subject changes
  useEffect(() => {
    setSelectedLesson(null);
    setLessonContent(null);
  }, [selectedSubject]);

  // Fetch curriculum for selected subject and grade
  const fetchCurriculum = useCallback(async (subjectId, track) => {
    if (!selectedGrade) return;
    
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      
      const response = await axios.get(
        `${API}/k12-program/curriculum/${subjectId}/${selectedGrade}`,
        {
          params: { track },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setCurriculum(response.data);
      setSelectedSubject({ id: subjectId, track });
    } catch (err) {
      console.error("Error fetching curriculum:", err);
      setError(err.response?.data?.detail || "Failed to load curriculum. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedGrade]);

  // Fetch lesson content
  const fetchLessonContent = async (lessonNumber) => {
    if (!selectedSubject || !selectedGrade) return;
    
    try {
      setLoadingLesson(true);
      const token = localStorage.getItem("token");
      
      const response = await axios.get(
        `${API}/k12-program/lesson/${selectedSubject.id}/${selectedGrade}/${lessonNumber}`,
        {
          params: { track: selectedSubject.track },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setLessonContent(response.data);
      setSelectedLesson(lessonNumber);
    } catch (err) {
      console.error("Error fetching lesson:", err);
      setError("Failed to load lesson content");
    } finally {
      setLoadingLesson(false);
    }
  };

  // Go back navigation
  const goBack = () => {
    if (lessonContent) {
      setLessonContent(null);
      setSelectedLesson(null);
    } else if (curriculum) {
      setCurriculum(null);
      setSelectedSubject(null);
    } else if (selectedGrade) {
      setSelectedGrade(null);
    }
  };

  // Get current subjects based on active tab
  const currentSubjects = activeTab === "academic" ? ACADEMIC_SUBJECTS : ZION_PILLARS;

  // Render lesson content view
  if (lessonContent && selectedLesson) {
    const content = lessonContent.content || {};
    const subjectInfo = currentSubjects.find(s => s.id === selectedSubject?.id);
    
    return (
      <div data-testid="k12-lesson-view" style={{ maxWidth: "900px", margin: "0 auto" }}>
        <div className="card" style={{ borderTop: `4px solid ${subjectInfo?.color || '#FFD700'}` }}>
          <button
            onClick={goBack}
            style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", marginBottom: "15px" }}
          >
            ← Back to Lessons
          </button>
          <h2 style={{ color: subjectInfo?.color || '#FFD700', margin: 0 }}>
            Lesson {selectedLesson}: {lessonContent.lesson_title || "Lesson"}
          </h2>
          <p style={{ color: "#888", marginTop: "5px" }}>
            {subjectInfo?.name} • Grade {selectedGrade}
          </p>
        </div>

        {/* Introduction */}
        {content.introduction && (
          <div className="card">
            <h3 style={{ color: "#FFD700" }}>Introduction</h3>
            <div
              className="lesson-section-content"
              style={{ lineHeight: "1.8", color: "#ddd" }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>{content.introduction}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Sections */}
        {content.sections?.map((section, idx) => (
          <div key={idx} className="card" style={{ borderLeft: `4px solid ${subjectInfo?.color || '#FFD700'}` }}>
            <h3 style={{ color: subjectInfo?.color || '#FFD700' }}>
              {typeof section === 'object' ? (section.title || section.heading || `Section ${idx + 1}`) : section}
            </h3>
            <div
              className="lesson-section-content"
              style={{ lineHeight: "1.8", color: "#ddd" }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {typeof section === 'object'
                  ? (section.content || section.text || section.body || '')
                  : ''}
              </ReactMarkdown>
            </div>
          </div>
        ))}

        {/* Key Points */}
        {content.key_points?.length > 0 && (
          <div className="card">
            <h3 style={{ color: "#90EE90" }}>Key Points</h3>
            <ul style={{ color: "#ddd", lineHeight: "2" }}>
              {content.key_points.map((point, idx) => (
                <li key={idx}>
                  {typeof point === 'object' 
                    ? (point.point || point.text || point.description || JSON.stringify(point))
                    : point
                  }
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Vocabulary */}
        {content.vocabulary?.length > 0 && (
          <div className="card">
            <h3 style={{ color: "#87CEEB" }}>Vocabulary</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
              {content.vocabulary.map((vocab, idx) => (
                <div key={idx} style={{ background: "rgba(135,206,235,0.1)", padding: "12px", borderRadius: "8px" }}>
                  <strong style={{ color: "#87CEEB" }}>
                    {typeof vocab === 'object' ? (vocab.term || vocab.word || '') : vocab}
                  </strong>
                  {typeof vocab === 'object' && vocab.definition && (
                    <p style={{ color: "#aaa", margin: "5px 0 0", fontSize: "0.9rem" }}>{vocab.definition}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activities */}
        {content.activities?.length > 0 && (
          <div className="card">
            <h3 style={{ color: "#DDA0DD" }}>Activities</h3>
            <ol style={{ color: "#ddd", lineHeight: "2" }}>
              {content.activities.map((activity, idx) => (
                <li key={idx}>
                  {typeof activity === 'object' 
                    ? (activity.activity || activity.description || activity.title || JSON.stringify(activity))
                    : activity
                  }
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* THE INVISIBLE TEACHER - Voice-First AI */}
        <InvisibleTeacher
          gradeLevel={selectedGrade}
          lessonContent={[
            content.introduction || '',
            ...(content.sections?.map(s => `${s.title}\n\n${s.content}`) || []),
          ].filter(Boolean).join('\n\n')}
          lessonTitle={lessonContent.lesson_title}
          user={user}
          autoStart={selectedGrade === "K" || selectedGrade === "1" || selectedGrade === "2"}
        />
      </div>
    );
  }

  // Render curriculum/lessons view
  if (curriculum && selectedSubject) {
    const subjectInfo = currentSubjects.find(s => s.id === selectedSubject.id);
    
    return (
      <div data-testid="k12-curriculum-view" style={{ maxWidth: "900px", margin: "0 auto" }}>
        <div className="card" style={{ borderTop: `4px solid ${subjectInfo?.color || '#FFD700'}` }}>
          <button
            onClick={goBack}
            style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", marginBottom: "15px" }}
          >
            ← Back to Subjects
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <span style={{ fontSize: "3rem" }}>{subjectInfo?.icon}</span>
            <div>
              <h2 style={{ color: subjectInfo?.color || '#FFD700', margin: 0 }}>{subjectInfo?.name}</h2>
              <p style={{ color: "#aaa", margin: "5px 0" }}>Grade {selectedGrade} Curriculum</p>
              {subjectInfo?.subtitle && (
                <p style={{ color: subjectInfo?.color, margin: 0, fontSize: "0.9rem" }}>{subjectInfo.subtitle}</p>
              )}
            </div>
          </div>
        </div>

        {/* Curriculum Overview */}
        {curriculum.overview && (
          <div className="card">
            <h3 style={{ color: "#FFD700" }}>Curriculum Overview</h3>
            <p style={{ color: "#ddd", lineHeight: "1.8" }}>{curriculum.overview}</p>
          </div>
        )}

        {/* Lessons */}
        <div className="card">
          <h3 style={{ color: subjectInfo?.color || '#FFD700' }}>
            Lessons ({curriculum.lessons?.length || 0})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "15px" }}>
            {curriculum.lessons?.map((lesson, idx) => (
              <button
                key={idx}
                data-testid={`lesson-${idx + 1}`}
                onClick={() => fetchLessonContent(lesson.number || idx + 1)}
                disabled={loadingLesson}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: `1px solid ${subjectInfo?.color}40`,
                  borderRadius: "8px",
                  padding: "15px",
                  color: "#ddd",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                  <span style={{
                    background: subjectInfo?.color || '#FFD700',
                    color: "#000",
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold"
                  }}>
                    {lesson.number || idx + 1}
                  </span>
                  <div>
                    <div style={{ fontWeight: "500", fontSize: "1.05rem" }}>{lesson.title}</div>
                    {lesson.description && (
                      <div style={{ color: "#888", fontSize: "0.9rem", marginTop: "3px" }}>
                        {lesson.description}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {loadingLesson && (
          <div className="card" style={{ textAlign: "center", padding: "40px" }}>
            <div style={{ fontSize: "2rem", marginBottom: "15px" }}>📖</div>
            <p style={{ color: subjectInfo?.color }}>Loading lesson content...</p>
          </div>
        )}
      </div>
    );
  }

  // Render grade selection if no grade selected
  if (!selectedGrade) {
    return (
      <div data-testid="k12-grade-selection" style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div className="card" style={{ 
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
          borderTop: "4px solid #FFD700",
          textAlign: "center"
        }}>
          <h1 style={{ color: "#FFD700", margin: "0 0 10px" }}>K-12 Academic Program</h1>
          <p style={{ color: "#aaa", maxWidth: "700px", margin: "0 auto", lineHeight: "1.7" }}>
            50-State Compliant Dual-Curriculum Education System combining Academic Foundations 
            with the Zion Pillars of Restoration Truth.
          </p>
        </div>

        {/* Grade Selection */}
        <div className="card">
          <h2 style={{ color: "#FFD700", marginBottom: "20px" }}>Select Your Grade Level</h2>
          
          {/* Primary School K-5 */}
          <div style={{ marginBottom: "30px" }}>
            <h3 style={{ color: "#4169E1", marginBottom: "15px", borderBottom: "1px solid #333", paddingBottom: "10px" }}>
              Primary School (K-5)
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
              {GRADE_LEVELS.filter(g => ["K", "1", "2", "3", "4", "5"].includes(g.id)).map(grade => (
                <button
                  key={grade.id}
                  data-testid={`grade-${grade.id}`}
                  onClick={() => setSelectedGrade(grade.id)}
                  style={{
                    background: "linear-gradient(135deg, #1a3a5c 0%, #0d2137 100%)",
                    border: "2px solid #4169E1",
                    borderRadius: "12px",
                    padding: "20px 15px",
                    color: "#fff",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s"
                  }}
                >
                  <div style={{ fontSize: "1.8rem", marginBottom: "8px" }}>🎒</div>
                  <div style={{ fontWeight: "bold", fontSize: "1.1rem" }}>{grade.name}</div>
                  <div style={{ color: "#87CEEB", fontSize: "0.85rem", marginTop: "5px" }}>Age {grade.age}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Middle School 6-8 */}
          <div style={{ marginBottom: "30px" }}>
            <h3 style={{ color: "#9932CC", marginBottom: "15px", borderBottom: "1px solid #333", paddingBottom: "10px" }}>
              Middle School (6-8)
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
              {GRADE_LEVELS.filter(g => ["6", "7", "8"].includes(g.id)).map(grade => (
                <button
                  key={grade.id}
                  data-testid={`grade-${grade.id}`}
                  onClick={() => setSelectedGrade(grade.id)}
                  style={{
                    background: "linear-gradient(135deg, #3a1a5c 0%, #1d0d37 100%)",
                    border: "2px solid #9932CC",
                    borderRadius: "12px",
                    padding: "20px 15px",
                    color: "#fff",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s"
                  }}
                >
                  <div style={{ fontSize: "1.8rem", marginBottom: "8px" }}>📚</div>
                  <div style={{ fontWeight: "bold", fontSize: "1.1rem" }}>{grade.name}</div>
                  <div style={{ color: "#DDA0DD", fontSize: "0.85rem", marginTop: "5px" }}>Age {grade.age}</div>
                </button>
              ))}
            </div>
          </div>

          {/* High School 9-12+ */}
          <div>
            <h3 style={{ color: "#8B0000", marginBottom: "15px", borderBottom: "1px solid #333", paddingBottom: "10px" }}>
              High School (9-12+)
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
              {GRADE_LEVELS.filter(g => ["9", "10", "11", "12", "12+"].includes(g.id)).map(grade => (
                <button
                  key={grade.id}
                  data-testid={`grade-${grade.id}`}
                  onClick={() => setSelectedGrade(grade.id)}
                  style={{
                    background: "linear-gradient(135deg, #5c1a1a 0%, #370d0d 100%)",
                    border: "2px solid #8B0000",
                    borderRadius: "12px",
                    padding: "20px 15px",
                    color: "#fff",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s"
                  }}
                >
                  <div style={{ fontSize: "1.8rem", marginBottom: "8px" }}>🎓</div>
                  <div style={{ fontWeight: "bold", fontSize: "1.1rem" }}>{grade.name}</div>
                  <div style={{ color: "#FF6B6B", fontSize: "0.85rem", marginTop: "5px" }}>Age {grade.age}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Compliance Notice */}
        <div className="card" style={{ background: "rgba(255,215,0,0.05)", borderLeft: "4px solid #FFD700" }}>
          <h3 style={{ color: "#FFD700", margin: "0 0 10px" }}>50-State Homeschool Compliance</h3>
          <p style={{ color: "#aaa", margin: 0, lineHeight: "1.7" }}>
            This dual-curriculum system meets legal requirements for homeschool education across all 50 states. 
            Track A (Academic Foundations) provides standard educational benchmarks while Track B (The Zion Pillars) 
            delivers specialized doctrinal instruction for the restoration of Yisrael.
          </p>
        </div>
      </div>
    );
  }

  // Render subject selection for selected grade (Two-Tab System)
  const gradeInfo = GRADE_LEVELS.find(g => g.id === selectedGrade);
  
  return (
    <div data-testid="k12-subject-selection" style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {/* Grade Header */}
      <div className="card" style={{ 
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
        borderTop: "4px solid #FFD700"
      }}>
        <button
          onClick={goBack}
          style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", marginBottom: "15px" }}
        >
          ← Back to Grade Selection
        </button>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ color: "#FFD700", margin: "0 0 5px" }}>{gradeInfo?.name}</h1>
          <p style={{ color: "#aaa", margin: 0 }}>Age {gradeInfo?.age} • K-12 Academic Program</p>
        </div>
      </div>

      {/* Two-Tab System */}
      <div className="card" style={{ padding: "0" }}>
        <div style={{ display: "flex", borderBottom: "2px solid #333" }}>
          <button
            data-testid="tab-academic"
            onClick={() => setActiveTab("academic")}
            style={{
              flex: 1,
              padding: "20px",
              background: activeTab === "academic" 
                ? "linear-gradient(135deg, #1a3a5c 0%, #0d2137 100%)" 
                : "transparent",
              border: "none",
              borderBottom: activeTab === "academic" ? "3px solid #4169E1" : "3px solid transparent",
              color: activeTab === "academic" ? "#fff" : "#666",
              cursor: "pointer",
              fontWeight: activeTab === "academic" ? "bold" : "normal",
              fontSize: "1.1rem",
              transition: "all 0.3s"
            }}
          >
            <span style={{ marginRight: "10px" }}>📚</span>
            ACADEMIC FOUNDATIONS
            <div style={{ fontSize: "0.75rem", color: activeTab === "academic" ? "#87CEEB" : "#555", marginTop: "5px" }}>
              State Compliance Track
            </div>
          </button>
          <button
            data-testid="tab-zion"
            onClick={() => setActiveTab("zion")}
            style={{
              flex: 1,
              padding: "20px",
              background: activeTab === "zion" 
                ? "linear-gradient(135deg, #3a2a1a 0%, #1a1505 100%)" 
                : "transparent",
              border: "none",
              borderBottom: activeTab === "zion" ? "3px solid #FFD700" : "3px solid transparent",
              color: activeTab === "zion" ? "#fff" : "#666",
              cursor: "pointer",
              fontWeight: activeTab === "zion" ? "bold" : "normal",
              fontSize: "1.1rem",
              transition: "all 0.3s"
            }}
          >
            <span style={{ marginRight: "10px" }}>⚔️</span>
            THE ZION PILLARS
            <div style={{ fontSize: "0.75rem", color: activeTab === "zion" ? "#FFD700" : "#555", marginTop: "5px" }}>
              YKBN Doctrine Track
            </div>
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ padding: "25px" }}>
          {error && (
            <div style={{ 
              background: "#4d1a1a", 
              borderLeft: "4px solid #ff6b6b", 
              padding: "15px", 
              borderRadius: "8px",
              marginBottom: "20px"
            }}>
              <p style={{ color: "#ff6b6b", margin: 0 }}>{error}</p>
            </div>
          )}

          {/* Subject Cards */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "20px" 
          }}>
            {currentSubjects.map(subject => (
              <div
                key={subject.id}
                data-testid={`subject-${subject.id}`}
                onClick={() => fetchCurriculum(subject.id, activeTab)}
                style={{
                  background: `linear-gradient(135deg, ${subject.color}15 0%, ${subject.color}05 100%)`,
                  border: `2px solid ${subject.color}40`,
                  borderRadius: "12px",
                  padding: "25px",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = `0 8px 25px ${subject.color}30`;
                  e.currentTarget.style.borderColor = subject.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = `${subject.color}40`;
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "15px", marginBottom: "15px" }}>
                  <span style={{ fontSize: "2.5rem" }}>{subject.icon}</span>
                  <div>
                    <h3 style={{ color: subject.color, margin: 0 }}>{subject.name}</h3>
                    {subject.subtitle && (
                      <p style={{ color: "#aaa", margin: "3px 0 0", fontSize: "0.85rem" }}>{subject.subtitle}</p>
                    )}
                  </div>
                </div>
                <p style={{ color: "#888", margin: 0, lineHeight: "1.6", fontSize: "0.95rem" }}>
                  {subject.description}
                </p>
                <div style={{ 
                  marginTop: "15px", 
                  paddingTop: "15px", 
                  borderTop: `1px solid ${subject.color}30`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <span style={{ color: "#666", fontSize: "0.85rem" }}>
                    Grade {selectedGrade} Curriculum
                  </span>
                  <span style={{ 
                    color: subject.color, 
                    fontSize: "0.9rem",
                    fontWeight: "bold"
                  }}>
                    View Lessons →
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>📖</div>
          <p style={{ color: "#FFD700", fontSize: "1.1rem" }}>Generating Grade {selectedGrade} curriculum...</p>
          <p style={{ color: "#888" }}>Preparing age-appropriate content</p>
        </div>
      )}
    </div>
  );
}

export default K12AcademicProgram;
